/**
 * HEIC/HEIF → JPEG conversion for in-browser previews (receipt pick, saved-attachment view).
 * The bytes shown are ONLY for display — whatever is uploaded/stored stays the original file.
 *
 * Uses `libheif-js/wasm-bundle` — a REAL WebAssembly build of libheif. The drop-in converter
 * packages (heic2any, heic-to) ship wasm2js builds (WebAssembly recompiled to plain JS), which
 * decode a phone photo several times slower. The decoder is lazy-imported so its ~1.4MB chunk
 * loads only when a HEIC image actually needs displaying.
 *
 * Conversions are cached per source Blob: the Attach Receipt modal remounts on every open and
 * the details modal re-renders freely — without the cache each pass re-decoded the same photo.
 */
const cache = new WeakMap<Blob, Promise<Blob>>();

export function heicPreviewBlob(source: Blob): Promise<Blob> {
  let pending = cache.get(source);
  if (!pending) {
    pending = convert(source);
    // A failed conversion must not poison the cache — let a later attempt retry.
    pending.catch(() => cache.delete(source));
    cache.set(source, pending);
  }
  return pending;
}

/**
 * Whether raw image bytes are a HEIF container — an ISO-BMFF `ftyp` box with a HEIF brand.
 * Needed because stored attachments can carry a generic content type (`application/octet-stream`)
 * when they were uploaded before the type was set explicitly.
 */
export function looksLikeHeif(bytes: Uint8Array): boolean {
  if (bytes.length < 12) return false;
  const ascii = (from: number, to: number) => String.fromCharCode(...bytes.subarray(from, to));
  if (ascii(4, 8) !== 'ftyp') return false;
  const brand = ascii(8, 12);
  return brand.startsWith('hei') || brand.startsWith('hev') || brand === 'mif1' || brand === 'msf1';
}

async function convert(source: Blob): Promise<Blob> {
  const { default: libheif } = await import('libheif-js/wasm-bundle');
  const decoder = new libheif.HeifDecoder();
  const images = decoder.decode(await source.arrayBuffer());
  const image = images[0];
  if (!image) throw new Error('empty HEIF container');
  const canvas = document.createElement('canvas');
  canvas.width = image.get_width();
  canvas.height = image.get_height();
  const context = canvas.getContext('2d');
  if (!context) throw new Error('canvas 2d unavailable');
  const imageData = context.createImageData(canvas.width, canvas.height);
  await new Promise<void>((resolve, reject) => {
    image.display(imageData, (result) => {
      if (result) resolve();
      else reject(new Error('HEIF decode failed'));
    });
  });
  context.putImageData(imageData, 0, 0);
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('JPEG encode failed'));
      },
      'image/jpeg',
      0.8,
    );
  });
}
